import React, { useCallback, useEffect, useState } from 'react';
import { Edit, Trash2 } from 'lucide-react';
import { adminSubscriptionsAPI } from '../../../lib/api';
import { formatCoef, formatMoney, formatSignedMoney, maskCardRequisites, normalizeDecimal, sumSignedMoney } from '../../../lib/subscriptionBilling';
import { useToast } from '../../../hooks/use-toast';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import { Checkbox } from '../../ui/checkbox';
import { Badge } from '../../ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';

function useDirectory(loader) {
  const { toast } = useToast();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      setRows((await loader()) || []);
    } catch (error) {
      toast({ title: 'Ошибка загрузки', description: error.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [loader, toast]);

  useEffect(() => {
    reload();
  }, [reload]);

  return { rows, loading, reload, toast };
}

function ActiveBox({ checked, onChange, label }) {
  return (
    <label className="flex items-center gap-2 text-sm">
      <Checkbox checked={checked} onCheckedChange={(value) => onChange(value === true)} />
      {label}
    </label>
  );
}

export function CompaniesDirectory() {
  const loader = useCallback(() => adminSubscriptionsAPI.getCompanies(), []);
  const { rows, loading, reload, toast } = useDirectory(loader);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ name: '', client_coefficient: '1.37', inn: '', is_active: true });

  const reset = () => {
    setEditingId(null);
    setForm({ name: '', client_coefficient: '1.37', inn: '', is_active: true });
  };

  const save = async (event) => {
    event.preventDefault();
    try {
      await adminSubscriptionsAPI.saveCompany(editingId, {
        name: form.name,
        client_coefficient: normalizeDecimal(form.client_coefficient),
        inn: form.inn,
        is_active: form.is_active,
      });
      toast({ title: 'Компания сохранена' });
      reset();
      await reload();
    } catch (error) {
      toast({ title: 'Не сохранилось', description: error.message, variant: 'destructive' });
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Переместить компанию «${row.name}» в корзину?`)) return;
    try {
      await adminSubscriptionsAPI.deleteCompany(row.id);
      if (editingId === row.id) reset();
      await reload();
    } catch (error) {
      toast({ title: 'Не удалось удалить', description: error.message, variant: 'destructive' });
    }
  };

  return (
    <div className="grid gap-4 lg:grid-cols-[320px_1fr]">
      <Card>
        <CardHeader><CardTitle>{editingId ? 'Компания' : 'Новая компания'}</CardTitle></CardHeader>
        <CardContent>
          <form onSubmit={save} className="space-y-3">
            <div className="space-y-2">
              <Label>Название</Label>
              <Input value={form.name} onChange={(event) => setForm((prev) => ({ ...prev, name: event.target.value }))} />
            </div>
            <div className="space-y-2">
              <Label>Коэффициент клиента</Label>
              <Input value={form.client_coefficient} onChange={(event) => setForm((prev) => ({ ...prev, client_coefficient: event.target.value }))} placeholder="1,37" />
              <p className="text-xs text-muted-foreground">Цена клиента = цена списания × коэффициент.</p>
            </div>
            <div className="space-y-2">
              <Label>ИНН</Label>
              <Input value={form.inn} onChange={(event) => setForm((prev) => ({ ...prev, inn: event.target.value }))} placeholder="10 или 12 цифр" />
              <p className="text-xs text-muted-foreground">Для автопривязки к профилю ЛК по ИНН.</p>
            </div>
            <ActiveBox checked={form.is_active} onChange={(is_active) => setForm((prev) => ({ ...prev, is_active }))} label="Активна" />
            <div className="flex gap-2">
              <Button type="submit">Сохранить</Button>
              {editingId && <Button type="button" variant="outline" onClick={reset}>Отмена</Button>}
            </div>
          </form>
        </CardContent>
      </Card>
      <Card>
        <CardContent className="pt-6">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Компания</TableHead>
                <TableHead>ИНН</TableHead>
                <TableHead>Коэффициент</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && <TableRow><TableCell colSpan={4}>Загрузка...</TableCell></TableRow>}
              {!loading && rows.map((row) => (
                <TableRow key={row.id}>
                  <TableCell>
                    {row.name} {!row.is_active && <Badge variant="secondary">скрыта</Badge>}
                  </TableCell>
                  <TableCell>{row.inn || '—'}</TableCell>
                  <TableCell>{formatCoef(row.client_coefficient)}</TableCell>
                  <TableCell className="text-right">
                    <Button type="button" size="icon" variant="ghost" aria-label="Исправить" onClick={() => { setEditingId(row.id); setForm({ name: row.name, client_coefficient: row.client_coefficient, inn: row.inn || '', is_active: row.is_active }); }}>
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button type="button" size="icon" variant="ghost" aria-label="Удалить" onClick={() => remove(row)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

export function ClientsDirectory() {
  const loadClients = useCallback(() => adminSubscriptionsAPI.getClients(), []);
  const loadCompanies = useCallback(() => adminSubscriptionsAPI.getCompanies(), []);
  const { rows, loading, reload, toast } = useDirectory(loadClients);
  const [companies, setCompanies] = useState([]);
  const [userOptions, setUserOptions] = useState([]);
  const [userQuery, setUserQuery] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ name: '', company_id: '', user_id: '', is_active: true });

  useEffect(() => {
    loadCompanies().then((data) => setCompanies(data || [])).catch(() => setCompanies([]));
  }, [loadCompanies]);

  useEffect(() => {
    let cancelled = false;
    const timer = setTimeout(() => {
      adminSubscriptionsAPI.searchUsers(userQuery)
        .then((data) => {
          if (!cancelled) setUserOptions(data || []);
        })
        .catch(() => {
          if (!cancelled) setUserOptions([]);
        });
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [userQuery]);

  const reset = () => {
    setEditingId(null);
    setForm({ name: '', company_id: '', user_id: '', is_active: true });
    setUserQuery('');
  };

  const save = async (event) => {
    event.preventDefault();
    if (!form.company_id) {
      toast({ title: 'Выберите компанию', variant: 'destructive' });
      return;
    }
    try {
      await adminSubscriptionsAPI.saveClient(editingId, {
        name: form.name,
        company_id: Number(form.company_id),
        user_id: form.user_id || null,
        is_active: form.is_active,
      });
      toast({ title: 'Клиент сохранён' });
      reset();
      await reload();
    } catch (error) {
      toast({ title: 'Не сохранилось', description: error.message, variant: 'destructive' });
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Переместить клиента «${row.name}» в корзину?`)) return;
    try {
      await adminSubscriptionsAPI.deleteClient(row.id);
      if (editingId === row.id) reset();
      await reload();
    } catch (error) {
      toast({ title: 'Не удалось удалить', description: error.message, variant: 'destructive' });
    }
  };

  return (
    <div className="grid gap-4 lg:grid-cols-[320px_1fr]">
      <Card>
        <CardHeader><CardTitle>{editingId ? 'Клиент' : 'Новый клиент'}</CardTitle></CardHeader>
        <CardContent>
          <form onSubmit={save} className="space-y-3">
            <div className="space-y-2">
              <Label>Имя</Label>
              <Input value={form.name} onChange={(event) => setForm((prev) => ({ ...prev, name: event.target.value }))} />
            </div>
            <div className="space-y-2">
              <Label>Компания</Label>
              <Select value={form.company_id || undefined} onValueChange={(value) => setForm((prev) => ({ ...prev, company_id: value }))}>
                <SelectTrigger><SelectValue placeholder="Компания" /></SelectTrigger>
                <SelectContent>
                  {companies.filter((item) => item.is_active || String(item.id) === String(form.company_id)).map((item) => (
                    <SelectItem key={item.id} value={String(item.id)}>{item.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Пользователь ЛК</Label>
              <Input
                value={userQuery}
                onChange={(event) => setUserQuery(event.target.value)}
                placeholder="Поиск: email, организация, ИНН"
              />
              <Select
                value={form.user_id || 'none'}
                onValueChange={(value) => setForm((prev) => ({ ...prev, user_id: value === 'none' ? '' : value }))}
              >
                <SelectTrigger><SelectValue placeholder="Не привязан" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Не привязан</SelectItem>
                  {form.user_id && !userOptions.some((item) => item.id === form.user_id) && (
                    <SelectItem value={form.user_id}>Текущий: {form.user_id}</SelectItem>
                  )}
                  {userOptions.map((item) => (
                    <SelectItem key={item.id} value={item.id}>
                      {item.email}
                      {item.organization_name ? ` · ${item.organization_name}` : ''}
                      {item.inn ? ` · ${item.inn}` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <ActiveBox checked={form.is_active} onChange={(is_active) => setForm((prev) => ({ ...prev, is_active }))} label="Активен" />
            <div className="flex gap-2">
              <Button type="submit">Сохранить</Button>
              {editingId && <Button type="button" variant="outline" onClick={reset}>Отмена</Button>}
            </div>
          </form>
        </CardContent>
      </Card>
      <Card>
        <CardContent className="pt-6">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Клиент</TableHead>
                <TableHead>Компания</TableHead>
                <TableHead>ЛК</TableHead>
                <TableHead>Баланс</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && <TableRow><TableCell colSpan={5}>Загрузка...</TableCell></TableRow>}
              {!loading && rows.map((row) => (
                <TableRow key={row.id}>
                  <TableCell>
                    {row.name} {!row.is_active && <Badge variant="secondary">скрыт</Badge>}
                  </TableCell>
                  <TableCell>{row.company_name}</TableCell>
                  <TableCell className="max-w-[200px] break-all text-sm">
                    {row.user_email || '—'}
                  </TableCell>
                  <TableCell>{formatSignedMoney(row.balance)}</TableCell>
                  <TableCell className="text-right">
                    <Button
                      type="button"
                      size="icon"
                      variant="ghost"
                      aria-label="Исправить"
                      onClick={() => {
                        setEditingId(row.id);
                        setForm({
                          name: row.name,
                          company_id: String(row.company_id),
                          user_id: row.user_id || '',
                          is_active: row.is_active,
                        });
                        setUserQuery(row.user_email || '');
                      }}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button type="button" size="icon" variant="ghost" aria-label="Удалить" onClick={() => remove(row)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {!loading && rows.length > 0 && (
            <p className="mt-3 text-sm">Сумма балансов: {formatSignedMoney(sumSignedMoney(rows.map((row) => row.balance)))}</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export function PartnersDirectory() {
  const loader = useCallback(() => adminSubscriptionsAPI.getPartners(), []);
  const { rows, loading, reload, toast } = useDirectory(loader);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({
    name: '',
    card_details: '',
    payment_address: '',
    partner_coefficient: '1.05',
    is_active: true,
  });

  const reset = () => {
    setEditingId(null);
    setForm({ name: '', card_details: '', payment_address: '', partner_coefficient: '1.05', is_active: true });
  };

  const save = async (event) => {
    event.preventDefault();
    try {
      await adminSubscriptionsAPI.savePartner(editingId, {
        name: form.name,
        card_details: form.card_details,
        payment_address: form.payment_address,
        partner_coefficient: normalizeDecimal(form.partner_coefficient),
        is_active: form.is_active,
      });
      toast({ title: 'Партнёр сохранён' });
      reset();
      await reload();
    } catch (error) {
      toast({ title: 'Не сохранилось', description: error.message, variant: 'destructive' });
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Переместить партнёра «${row.name}» в корзину?`)) return;
    try {
      await adminSubscriptionsAPI.deletePartner(row.id);
      if (editingId === row.id) reset();
      await reload();
    } catch (error) {
      toast({ title: 'Не удалось удалить', description: error.message, variant: 'destructive' });
    }
  };

  return (
    <div className="grid gap-4 lg:grid-cols-[360px_1fr]">
      <Card>
        <CardHeader><CardTitle>{editingId ? 'Партнёр' : 'Новый партнёр'}</CardTitle></CardHeader>
        <CardContent>
          <form onSubmit={save} className="space-y-3">
            <div className="space-y-2">
              <Label>Название</Label>
              <Input value={form.name} onChange={(event) => setForm((prev) => ({ ...prev, name: event.target.value }))} />
            </div>
            <div className="space-y-2">
              <Label>Реквизиты карты</Label>
              <Textarea value={form.card_details} onChange={(event) => setForm((prev) => ({ ...prev, card_details: event.target.value }))} />
            </div>
            <div className="space-y-2">
              <Label>Платёжный адрес</Label>
              <Textarea value={form.payment_address} onChange={(event) => setForm((prev) => ({ ...prev, payment_address: event.target.value }))} />
            </div>
            <div className="space-y-2">
              <Label>Коэффициент партнёра</Label>
              <Input value={form.partner_coefficient} onChange={(event) => setForm((prev) => ({ ...prev, partner_coefficient: event.target.value }))} placeholder="1,05" />
              <p className="text-xs text-muted-foreground">Цена партнёра = цена списания × коэффициент.</p>
            </div>
            <ActiveBox checked={form.is_active} onChange={(is_active) => setForm((prev) => ({ ...prev, is_active }))} label="Активен" />
            <div className="flex gap-2">
              <Button type="submit">Сохранить</Button>
              {editingId && <Button type="button" variant="outline" onClick={reset}>Отмена</Button>}
            </div>
          </form>
        </CardContent>
      </Card>
      <Card>
        <CardContent className="pt-6 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Партнёр</TableHead>
                <TableHead>Реквизиты</TableHead>
                <TableHead>Адрес</TableHead>
                <TableHead>Коэффициент</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && <TableRow><TableCell colSpan={5}>Загрузка...</TableCell></TableRow>}
              {!loading && rows.map((row) => (
                <TableRow key={row.id}>
                  <TableCell>
                    {row.name} {!row.is_active && <Badge variant="secondary">скрыт</Badge>}
                  </TableCell>
                  <TableCell className="max-w-[180px] break-all">{maskCardRequisites(row.card_details)}</TableCell>
                  <TableCell className="max-w-[180px] break-all">{row.payment_address}</TableCell>
                  <TableCell>{formatCoef(row.partner_coefficient)}</TableCell>
                  <TableCell className="text-right">
                    <Button type="button" size="icon" variant="ghost" aria-label="Исправить" onClick={() => {
                      setEditingId(row.id);
                      setForm({
                        name: row.name,
                        card_details: row.card_details,
                        payment_address: row.payment_address,
                        partner_coefficient: row.partner_coefficient,
                        is_active: row.is_active,
                      });
                    }}>
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button type="button" size="icon" variant="ghost" aria-label="Удалить" onClick={() => remove(row)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

export function ServicesDirectory() {
  const loadServices = useCallback(() => adminSubscriptionsAPI.getServices(), []);
  const loadTariffs = useCallback(() => adminSubscriptionsAPI.getTariffs(), []);
  const servicesState = useDirectory(loadServices);
  const tariffsState = useDirectory(loadTariffs);
  const [serviceForm, setServiceForm] = useState({ name: '', is_active: true });
  const [serviceId, setServiceId] = useState(null);
  const [tariffForm, setTariffForm] = useState({
    service_id: '',
    name: '',
    default_charge_price: '',
    is_active: true,
  });
  const [tariffId, setTariffId] = useState(null);

  const saveService = async (event) => {
    event.preventDefault();
    try {
      await adminSubscriptionsAPI.saveService(serviceId, {
        name: serviceForm.name,
        is_active: serviceForm.is_active,
      });
      servicesState.toast({ title: 'Сервис сохранён' });
      setServiceId(null);
      setServiceForm({ name: '', is_active: true });
      await servicesState.reload();
    } catch (error) {
      servicesState.toast({ title: 'Не сохранилось', description: error.message, variant: 'destructive' });
    }
  };

  const saveTariff = async (event) => {
    event.preventDefault();
    if (!tariffForm.service_id) {
      tariffsState.toast({ title: 'Выберите сервис', variant: 'destructive' });
      return;
    }
    try {
      await adminSubscriptionsAPI.saveTariff(tariffId, {
        service_id: Number(tariffForm.service_id),
        name: tariffForm.name,
        default_charge_price: tariffForm.default_charge_price ? normalizeDecimal(tariffForm.default_charge_price) : null,
        is_active: tariffForm.is_active,
      });
      tariffsState.toast({ title: 'Тариф сохранён' });
      setTariffId(null);
      setTariffForm({ service_id: '', name: '', default_charge_price: '', is_active: true });
      await tariffsState.reload();
    } catch (error) {
      tariffsState.toast({ title: 'Не сохранилось', description: error.message, variant: 'destructive' });
    }
  };

  const removeService = async (row) => {
    if (!window.confirm(`Переместить сервис «${row.name}» в корзину?`)) return;
    try {
      await adminSubscriptionsAPI.deleteService(row.id);
      await servicesState.reload();
    } catch (error) {
      servicesState.toast({ title: 'Не удалось удалить', description: error.message, variant: 'destructive' });
    }
  };

  const removeTariff = async (row) => {
    if (!window.confirm(`Переместить тариф «${row.name}» в корзину?`)) return;
    try {
      await adminSubscriptionsAPI.deleteTariff(row.id);
      await tariffsState.reload();
    } catch (error) {
      tariffsState.toast({ title: 'Не удалось удалить', description: error.message, variant: 'destructive' });
    }
  };

  return (
    <div className="space-y-6">
      <div className="grid gap-4 lg:grid-cols-[320px_1fr]">
        <Card>
          <CardHeader><CardTitle>{serviceId ? 'Сервис' : 'Новый сервис'}</CardTitle></CardHeader>
          <CardContent>
            <form onSubmit={saveService} className="space-y-3">
              <div className="space-y-2">
                <Label>Название</Label>
                <Input value={serviceForm.name} onChange={(event) => setServiceForm((prev) => ({ ...prev, name: event.target.value }))} />
              </div>
              <ActiveBox checked={serviceForm.is_active} onChange={(is_active) => setServiceForm((prev) => ({ ...prev, is_active }))} label="Активен" />
              <div className="flex gap-2">
                <Button type="submit">Сохранить</Button>
                {serviceId && <Button type="button" variant="outline" onClick={() => { setServiceId(null); setServiceForm({ name: '', is_active: true }); }}>Отмена</Button>}
              </div>
            </form>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <Table>
              <TableHeader><TableRow><TableHead>Сервис</TableHead><TableHead /></TableRow></TableHeader>
              <TableBody>
                {servicesState.loading && <TableRow><TableCell colSpan={2}>Загрузка...</TableCell></TableRow>}
                {!servicesState.loading && servicesState.rows.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>{row.name} {!row.is_active && <Badge variant="secondary">скрыт</Badge>}</TableCell>
                    <TableCell className="text-right">
                      <Button type="button" size="icon" variant="ghost" aria-label="Исправить" onClick={() => { setServiceId(row.id); setServiceForm({ name: row.name, is_active: row.is_active }); }}>
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button type="button" size="icon" variant="ghost" aria-label="Удалить" onClick={() => removeService(row)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-4 lg:grid-cols-[360px_1fr]">
        <Card>
          <CardHeader><CardTitle>{tariffId ? 'Тариф' : 'Новый тариф'}</CardTitle></CardHeader>
          <CardContent>
            <form onSubmit={saveTariff} className="space-y-3">
              <div className="space-y-2">
                <Label>Сервис</Label>
                <Select value={tariffForm.service_id || undefined} onValueChange={(value) => setTariffForm((prev) => ({ ...prev, service_id: value }))}>
                  <SelectTrigger><SelectValue placeholder="Сервис" /></SelectTrigger>
                  <SelectContent>
                    {servicesState.rows.filter((item) => item.is_active || String(item.id) === String(tariffForm.service_id)).map((item) => (
                      <SelectItem key={item.id} value={String(item.id)}>{item.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Название</Label>
                <Input value={tariffForm.name} onChange={(event) => setTariffForm((prev) => ({ ...prev, name: event.target.value }))} />
              </div>
              <div className="space-y-2">
                <Label>Цена списания по умолчанию</Label>
                <Input value={tariffForm.default_charge_price} onChange={(event) => setTariffForm((prev) => ({ ...prev, default_charge_price: event.target.value }))} placeholder="необязательно" />
              </div>
              <ActiveBox checked={tariffForm.is_active} onChange={(is_active) => setTariffForm((prev) => ({ ...prev, is_active }))} label="Активен" />
              <div className="flex gap-2">
                <Button type="submit">Сохранить</Button>
                {tariffId && (
                  <Button type="button" variant="outline" onClick={() => {
                    setTariffId(null);
                    setTariffForm({ service_id: '', name: '', default_charge_price: '', is_active: true });
                  }}>Отмена</Button>
                )}
              </div>
            </form>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Сервис</TableHead>
                  <TableHead>Тариф</TableHead>
                  <TableHead>Цена по умолчанию</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {tariffsState.loading && <TableRow><TableCell colSpan={4}>Загрузка...</TableCell></TableRow>}
                {!tariffsState.loading && tariffsState.rows.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>{row.service_name}</TableCell>
                    <TableCell>{row.name} {!row.is_active && <Badge variant="secondary">скрыт</Badge>}</TableCell>
                    <TableCell>{row.default_charge_price ? formatMoney(row.default_charge_price) : '—'}</TableCell>
                    <TableCell className="text-right">
                      <Button type="button" size="icon" variant="ghost" aria-label="Исправить" onClick={() => {
                        setTariffId(row.id);
                        setTariffForm({
                          service_id: String(row.service_id),
                          name: row.name,
                          default_charge_price: row.default_charge_price || '',
                          is_active: row.is_active,
                        });
                      }}>
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button type="button" size="icon" variant="ghost" aria-label="Удалить" onClick={() => removeTariff(row)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
